import { memo, useCallback } from 'react'

import { Badge } from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useSnackbar } from 'notistack'
import { useTranslation } from 'react-i18next'

import { Header } from '@shared/components/Header'
import { Tabs } from '@shared/components/MaterialUIEnhancements/Tabs'
import { Content } from '@shared/layouts'
import { NavigationTabLink } from 'components/LinkedMUIComponents/NavigationTabLink'
import { ActionsMenu } from 'domains/Workspaces/Events/Event'
import { useEventUpdateMutation } from 'hooks/mutations/smep'
import {
  eventConfigRoute,
  eventDashboardRoute,
  eventExpManifestRoute,
  eventMonitoringRoute,
  eventRawRoute,
  eventRoute,
  eventSimpleConfigRoute,
  eventWatchRoute,
} from 'pages/NWPlatform/Workspaces/Workspace/Events/Event'
import { eventQueryKeys } from 'services/queryKeys'

const StructureRaw = () => {
  const { t } = useTranslation(['pages', 'entity'])
  const { enqueueSnackbar } = useSnackbar()

  const { eventId } = eventRoute.useParams()

  const eventQuery = useQuery(eventQueryKeys.detail({ eventId }))

  const { data: event } = eventQuery

  const { mutate: updateEvent, ...eventUpdateMutation } = useEventUpdateMutation()

  const handleUpdateEventName = useCallback(
    (title: string, closeCallback: () => void) => {
      updateEvent(
        {
          eventId: eventId,
          smepEventEdit: { name: title },
        },
        {
          onError: () => enqueueSnackbar(t('pages:Workspace.updateError')),
          onSettled: closeCallback,
        },
      )
    },
    [t, updateEvent, eventId, enqueueSnackbar],
  )

  return (
    <Content
      HeaderSlot={
        <Header
          title={eventQuery.data?.name ?? ''}
          isLoading={eventQuery.isLoading}
          isSaving={eventUpdateMutation.isPending}
          onSave={handleUpdateEventName}
          Actions={<ActionsMenu />}
        />
      }
      SubNavigation={
        <Tabs>
          <NavigationTabLink from={eventRoute.to} to={eventDashboardRoute.to}>
            {t('pages:Workspace.general')}
          </NavigationTabLink>
          <NavigationTabLink from={eventRoute.to} to={eventWatchRoute.to}>
            {t('pages:Workspace.watch')}
          </NavigationTabLink>
          <NavigationTabLink from={eventRoute.to} to={eventMonitoringRoute.to}>
            <Badge variant="dot" badgeContent={event?.alerts?.length} color="error">
              {t('pages:Workspace.monitoring')}
            </Badge>
          </NavigationTabLink>
          <NavigationTabLink from={eventRoute.to} to={eventConfigRoute.to}>
            {t('pages:Workspace.config')}
          </NavigationTabLink>
          <NavigationTabLink from={eventRoute.to} to={eventSimpleConfigRoute.to}>
            {t('pages:Workspace.simpleConfig')}
          </NavigationTabLink>
          <NavigationTabLink from={eventRoute.to} to={eventRawRoute.to}>
            {t('pages:Workspace.status')}
          </NavigationTabLink>
          <NavigationTabLink from={eventRoute.to} to={eventExpManifestRoute.to}>
            {t('pages:Workspace.expManifest')}
          </NavigationTabLink>
        </Tabs>
      }
    />
  )
}

export const Structure = memo(StructureRaw)
